import Panel from '@/components/_core/ui/context-selection/components/panel';
import Vue from 'vue';
import { mapGetters, mapState } from 'vuex';
import { ACCESS_LEVEL } from '~/constants/_core/access-level';
import { OWNER } from '~/constants/_core/owner';
import { buildDefaultAuthorizeUrl } from '~/helpers/_core/safea-v3';

const STEPS = {
  CONTEXT     : 'context',
  TENANT      : 'tenant',
  INSTITUTION : 'institution'
};

export default Vue.extend({
  components : { Panel },
  props      : {
    showLogo: {
      type    : Boolean,
      default : false
    }
  },
  data() {
    return {
      step                   : STEPS.CONTEXT,
      currentTenantUuid      : null,
      currentInstitutionUuid : null,
      loading                : false
    };
  },
  created() {
    this.STEPS = STEPS;
    this.OWNER = OWNER;
  },
  computed: {
    ...mapState({
      currentContext : state => state._core.user.currentContext,
      contexts       : state => state._core.user.contexts,
      isOperation    : state => state._core.application.isOperation,
      owner          : state => state._core.application.owner,
      accessibility  : state => state._core.user.accessibility
    }),
    ...mapGetters({
      currentAccessLevel: '_core/user/getCurrentAccessLevel'
    }),
    accessLevels() {
      const institutions = this.contexts.flatMap(ctx => ctx.institutions ?? []);
      const levels = [];
      if (this.isOperation && !!this.applicationContext) {
        levels.push({
          name      : this.$t('_core.contextSelection.application'),
          level     : ACCESS_LEVEL.APPLICATION,
          disabled  : this.currentAccessLevel === ACCESS_LEVEL.APPLICATION,
          navigable : false
        });
      }

      if (this.owner !== OWNER.IES && this.tenants.length > 0) {
        levels.push({
          name      : this.$t('_core.contextSelection.tenant.title'),
          level     : ACCESS_LEVEL.TENANT,
          disabled  : this.owner === OWNER.TENANT && this.currentAccessLevel === ACCESS_LEVEL.TENANT,
          navigable : this.tenants.length > 1
        });
      }

      if (institutions.length > 0) {
        levels.push({
          name      : this.$t('_core.contextSelection.institution.title'),
          level     : ACCESS_LEVEL.INSTITUTION,
          disabled  : this.currentAccessLevel === ACCESS_LEVEL.INSTITUTION && institutions.length === 1,
          navigable : true
        });
      }

      return levels;
    },
    subtitle() {
      if (this.step === STEPS.INSTITUTION) {
        return this.$t('_core.contextSelection.institution.title');
      }

      if (this.step === STEPS.TENANT) {
        return this.$t('_core.contextSelection.tenant.title');
      }

      return null;
    },
    disableApply() {
      if (!this.currentTenantUuid || (this.step === STEPS.INSTITUTION && !this.currentInstitutionUuid)) {
        return true;
      }

      if (this.step === STEPS.INSTITUTION && this.currentContext.institution?.uuid) {
        return this.currentContext.institution.uuid === this.currentInstitutionUuid;
      }

      return this.step === STEPS.TENANT
        && this.currentContext.tenant?.uuid === this.currentTenantUuid
        && this.currentContext.institution?.uuid === this.currentInstitutionUuid;
    },
    applicationContext() {
      return this.contexts.find(ctx => ctx.level === ACCESS_LEVEL.APPLICATION);
    },
    tenants() {
      if (this.step === STEPS.TENANT) {
        return this.contexts.filter(ctx => ctx.canAccess && ctx.level === ACCESS_LEVEL.TENANT);
      }

      return this.contexts.filter(ctx => ctx.level === ACCESS_LEVEL.TENANT);
    },
    institutions() {
      return this.contexts.find(ctx => ctx.uuid === this.currentTenantUuid)?.institutions ?? [];
    }
  },
  methods: {
    filterByAliasOrName(input, obj) {
      if (!input) {
        return obj;
      }

      return _.some([obj.alias, obj.name], (value) =>
        _.includes(value.toLowerCase(), input.toLowerCase())
      );
    },
    getLogo(option) {
      const isDark = this.accessibility?.darkThemeEnabled ?? false;
      return isDark ? (option.darkLogo ?? option.logo) : option.logo;
    },
    handleContextChange(level) {
      if (level === ACCESS_LEVEL.TENANT) {
        this.currentTenantUuid = this.currentContext.tenant.uuid;
        this.currentInstitutionUuid = null;
      } else if (level === ACCESS_LEVEL.INSTITUTION) {
        this.currentTenantUuid = this.currentContext.tenant.uuid;
        this.currentInstitutionUuid = this.currentContext.institution.uuid;
      } else if (level === ACCESS_LEVEL.APPLICATION) {
        this.currentTenantUuid = null;
        this.currentInstitutionUuid = null;
      }

      if (
        level === ACCESS_LEVEL.APPLICATION
        || (level === ACCESS_LEVEL.TENANT && this.owner === OWNER.TENANT)
      ) {
        return this.handleApply();
      }

      this.step = level;
    },
    async handleApply() {
      this.loading = true;
      const url = await buildDefaultAuthorizeUrl({ route: this.$route, store: this.$store }, {
        tenantUuid      : this.currentTenantUuid,
        institutionUuid : this.currentInstitutionUuid
      });
      window.location.assign(url);
    }
  },
  watch: {
    currentTenantId(curr, prev) {
      if (curr !== prev && prev !== null) {
        this.currentInstitutionUuid = null;
      }
    }
  }
});
